import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";

import { getNetTotalEnergyConsumption } from "utils/results";
import {
    getUpgradeDhwRowLabels,
    getUpgradeEnvelopeRowLabels,
    getUpgradeHvacRowLabels,
    getUpgradeOtherRowLabels,
} from "utils/upgrades";

import { selectResults } from "features/Model/Review/Results/_ducks/selectors";
import { selectNzStudyReportPdfData, selectModelData } from "features/Model/_ducks/selectors";
import { selectUpgradePackages } from "features/Model/Upgrades/_ducks/selectors";

import { actions as modelActions } from "features/Model/_ducks";

import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import Textarea from "components/Input/Textarea";

import Input from "components/Input";
import Button from "components/Button";

import classes from "../../styles.module.scss";

const { updateNzStudyPdfData } = modelActions;

const rowsToAdd = [
    { id: "energyConsumption", label: "Energy Consumption" },
    { id: "comfort", label: "Comfort" },
    { id: "buildingCondition", label: "Building Condition" },
    { id: "generation", label: "Renewables" },
    { id: "wall", label: "Wall Insulation" },
    { id: "ceiling", label: "Ceiling Insulation" },
    { id: "basement", label: "Foundation Insulation" },
    { id: "window", label: "Windows" },
    { id: "ventilation", label: "Ventilation" },
    { id: "spaceHeating", label: "Heating" },
    { id: "spaceCooling", label: "Cooling" },
    { id: "heatLossAndGain", label: "Heat Loss & Gain" },
    { id: "domesticHotWater", label: "Hot Water" },
    { id: "electricalPanelSize", label: "Electrical Panel Size (A)" },
];

const NetZeroPlanTable = ({
    results,
    modelData,
    upgradePackages,
    nzStudyReportPdfData,
    updateNzStudyPdfData,
    eFileResults = {},
    eFileModelData = {},
    eFileUpgradePackages = {},
}) => {
    const {
        selectedUpgrade = "",
        beforeComfort = "",
        afterComfort = "",
        beforeBuildingCondition = "",
        afterBuildingCondition = "",
        beforeElectricalPanelSize = "",
        afterElectricalPanelSize = "",
        nzPage2Content = {},
    } = nzStudyReportPdfData;

    const [currentSelectedUpgrade, setCurrentSelectedUpgrade] = useState(selectedUpgrade);

    // Determine if selected upgrade is from E-file
    const isEFileUpgrade = selectedUpgrade.startsWith("eFile_");
    const actualUpgradeKey = isEFileUpgrade ? selectedUpgrade.replace("eFile_", "") : selectedUpgrade;

    // When this is provided (not empty), we need to get labels for the post-retrofit upgrade column from the e-file base model data
    const eFileBaseModelData = isEFileUpgrade && actualUpgradeKey === "base" ? eFileModelData : {};

    // Use E-file data if selected upgrade is from E-file, otherwise use current model data
    const postRetrofitPackageResults = isEFileUpgrade ? eFileResults : results?.packageResults || {};
    const postRetrofitUpgradePackages = isEFileUpgrade ? eFileUpgradePackages : upgradePackages;

    const { modelData: preRetrofitModelDataFromResults = {}, resultsData: preRetrofitResultsData = {} } =
        results?.packageResults?.base || {};

    const { modelData: postRetrofitModelDataFromResults = {}, resultsData: postRetrofitResultsData = {} } =
        postRetrofitPackageResults[actualUpgradeKey] || {};

    // By definition, this is the post retrofit upgrade packages
    const upgradePackage = postRetrofitUpgradePackages?.[actualUpgradeKey] || {};

    // All of this comes from the pre retrofit file
    const {
        components = {}, //pre retrofit file
        airTightness = {}, //pre retrofit file
        codes = {}, //pre retrofit file
    } = modelData || {};

    const {
        domesticHotWater = {}, //pre retrofit file
        heatingCooling = {}, //pre retrofit file
        ventilation = {}, //pre retrofit file
        generation = {}, //pre retrofit file
        baseLoads = {}, //pre retrofit file
        program = {}, //pre retrofit file
    } = modelData || {};

    const tableRows = useMemo(() => {
        const rows = [];

        rowsToAdd.forEach(({ id: rowId, label: rowLabel }) => {
            if (rowId === "energyConsumption") {
                rows.push([rowLabel, "nzBeforeEnergy", "nzAfterEnergy"]);
            }

            if (rowId === "heatLossAndGain") {
                rows.push([rowLabel, "nzBeforeHeatLossGain", "nzAfterHeatLossGain"]);
            }

            if (rowId === "comfort") {
                rows.push([rowLabel, "beforeComfort", "afterComfort"]);
            }

            if (rowId === "buildingCondition") {
                rows.push([rowLabel, "beforeBuildingCondition", "afterBuildingCondition"]);
            }

            if (rowId === "electricalPanelSize") {
                rows.push([rowLabel, "beforeElectricalPanelSize", "afterElectricalPanelSize"]);
            }

            if (rowId === "generation") {
                rows.push([rowLabel, "nzBeforeGeneration", "nzAfterGeneration"]);
            }

            if (rowId === "wall") {
                rows.push([rowLabel, "nzBeforeWall", "nzAfterWall"]);
            }

            if (rowId === "ceiling") {
                rows.push([rowLabel, "nzBeforeCeiling", "nzAfterCeiling"]);
            }

            if (rowId === "basement") {
                rows.push([rowLabel, "nzBeforeFoundation", "nzAfterFoundation"]);
            }

            if (rowId === "window") {
                rows.push([rowLabel, "nzBeforeWindow", "nzAfterWindow"]);
            }

            if (rowId === "ventilation") {
                rows.push([rowLabel, "nzBeforeVentilation", "nzAfterVentilation"]);
            }

            if (rowId === "spaceHeating") {
                rows.push([rowLabel, `nzBeforeHeating`, `nzAfterHeating`]);
            }

            if (rowId === "spaceCooling") {
                rows.push([rowLabel, `nzBeforeCooling`, `nzAfterCooling`]);
            }

            if (rowId === "domesticHotWater") {
                rows.push([rowLabel, "nzBeforeDHW", "nzAfterDHW"]);
            }
        });

        return rows;
    }, []);

    // Initialize content for nzPage2Content from model and upgrade data
    const initContent = useMemo(() => {
        const { upgrades = {} } = upgradePackage;

        const generatedValues = {};

        const postRetrofitUpgradeCaseKey = isEmpty(eFileBaseModelData) ? "socAllUpgrades" : "socBaseCase";

        rowsToAdd.forEach(({ id: rowId, label: rowLabel }) => {
            if (rowId === "energyConsumption") {
                const { zeroFloorTotal: netTotalConsumption = 0 } = getNetTotalEnergyConsumption(
                    postRetrofitResultsData[postRetrofitUpgradeCaseKey] || {}
                );
                const { zeroFloorTotal: netBaseConsumption = 0 } = getNetTotalEnergyConsumption(
                    preRetrofitResultsData["socBaseCase"] || {}
                );

                generatedValues["nzBeforeEnergy"] = `${netBaseConsumption.toFixed(0)} GJ/year`;
                generatedValues["nzAfterEnergy"] = `${netTotalConsumption.toFixed(0)} GJ/year`;
            }

            if (rowId === "heatLossAndGain") {
                const { f280Results: { heatLoss = 0, latHeatGain = 0 } = {} } =
                    postRetrofitResultsData?.[postRetrofitUpgradeCaseKey] || {};
                const { f280Results: { heatLoss: baseHeatLoss = 0, latHeatGain: baseLatHeatGain = 0 } = {} } =
                    preRetrofitResultsData?.["socBaseCase"] || {};

                const baseHeatLossAndGainLabel = `HL: ${(baseHeatLoss * 3.41).toFixed(0)} Btu/h, HG: ${(
                    baseLatHeatGain * 3.41
                ).toFixed(0)} Btu/h`;
                const upgradedHeatLossAndGainLabel = `HL: ${(heatLoss * 3.41).toFixed(0)} Btu/h, HG: ${(
                    latHeatGain * 3.41
                ).toFixed(0)} Btu/h`;

                generatedValues["nzBeforeHeatLossGain"] = baseHeatLossAndGainLabel;
                generatedValues["nzAfterHeatLossGain"] = upgradedHeatLossAndGainLabel;
            }

            // User Input, stored separately from nzPage2Content
            // if (rowId === "comfort") {
            //     generatedValues["beforeComfort"] = beforeComfort;
            //     generatedValues["afterComfort"] = afterComfort;
            // }

            // User Input, stored separately from nzPage2Content
            // if (rowId === "buildingCondition") {
            //     generatedValues["beforeBuildingCondition"] = beforeBuildingCondition;
            //     generatedValues["afterBuildingCondition"] = afterBuildingCondition;
            // }

            // User Input, stored separately from nzPage2Content
            // if (rowId === "electricalPanelSize") {
            //     generatedValues["beforeElectricalPanelSize"] = `${beforeElectricalPanelSize} A`;
            //     generatedValues["afterElectricalPanelSize"] = `${afterElectricalPanelSize} A`;
            // }

            if (rowId === "generation") {
                let upgradedPVLabel = "No Change";

                if (!isEmpty(eFileBaseModelData)) {
                    upgradedPVLabel =
                        getUpgradeOtherRowLabels({
                            generation: eFileBaseModelData?.generation || {},
                            program: eFileBaseModelData?.program || {},
                            baseLoads: eFileBaseModelData?.baseLoads || {},
                            rowId: "generation",
                            upgradePackage: {},
                        })?.["label"] || "None";
                } else {
                    Object.keys(upgrades).forEach((key) => {
                        const { label = {}, upgradeType = "" } = upgrades[key] || {};

                        if (upgradeType === "generation") {
                            upgradedPVLabel = label[Object.keys(label)[0]] || "No Change";
                        }
                    });
                }

                const basePvLabel =
                    getUpgradeOtherRowLabels({
                        generation,
                        program,
                        baseLoads,
                        rowId: "generation",
                        upgradePackage: {},
                    })?.["label"] || "None";

                generatedValues["nzBeforeGeneration"] = basePvLabel;
                generatedValues["nzAfterGeneration"] = upgradedPVLabel;
            }

            if (rowId === "wall") {
                let upgradedWallLabel = "No Change";

                if (!isEmpty(eFileBaseModelData)) {
                    upgradedWallLabel =
                        getUpgradeEnvelopeRowLabels({
                            codes: eFileBaseModelData?.codes || {},
                            components: eFileBaseModelData?.components || {},
                            rowId: "wall",
                            upgradePackage: {},
                        })?.["label"] || "None";
                } else {
                    Object.keys(upgrades).forEach((key) => {
                        const { label = {}, upgradeType = "" } = upgrades[key] || {};

                        if (upgradeType === "wall") {
                            upgradedWallLabel = label[Object.keys(label)[0]] || "No Change";
                        }
                    });
                }

                const baseWallLabel =
                    getUpgradeEnvelopeRowLabels({
                        codes,
                        components,
                        rowId: "wall",
                        upgradePackage: {},
                    })?.["label"] || "None";

                generatedValues["nzBeforeWall"] = baseWallLabel;
                generatedValues["nzAfterWall"] = upgradedWallLabel;
            }

            if (rowId === "ceiling") {
                let upgradedCeilingLabel = "No Change";

                if (!isEmpty(eFileBaseModelData)) {
                    upgradedCeilingLabel =
                        getUpgradeEnvelopeRowLabels({
                            codes: eFileBaseModelData?.codes || {},
                            components: eFileBaseModelData?.components || {},
                            airtightness: eFileBaseModelData?.airTightness || {},
                            rowId: "ceilingWithAttic",
                            upgradePackage: {},
                        })?.["label"] || "None";
                } else {
                    Object.keys(upgrades).forEach((key) => {
                        const { label = {}, upgradeType = "" } = upgrades[key] || {};

                        if (upgradeType === "ceiling") {
                            upgradedCeilingLabel = label[Object.keys(label)[0]] || "No Change";
                        }
                    });
                }

                const baseCeilingLabel =
                    getUpgradeEnvelopeRowLabels({
                        codes,
                        components,
                        airtightness: airTightness,
                        rowId: "ceilingWithAttic",
                        upgradePackage: {},
                    })?.["label"] || "None";

                generatedValues["nzBeforeCeiling"] = baseCeilingLabel;
                generatedValues["nzAfterCeiling"] = upgradedCeilingLabel;
            }

            if (rowId === "basement") {
                let upgradedFoundationLabel = "No Change";

                if (!isEmpty(eFileBaseModelData)) {
                    upgradedFoundationLabel =
                        getUpgradeEnvelopeRowLabels({
                            codes: eFileBaseModelData?.codes || {},
                            components: eFileBaseModelData?.components || {},
                            rowId: "basementWall",
                            upgradePackage: {},
                        })?.["label"] || "None";
                } else {
                    Object.keys(upgrades).forEach((key) => {
                        const { label = {}, upgradeType = "" } = upgrades[key] || {};

                        if (upgradeType === "basement") {
                            upgradedFoundationLabel = label[Object.keys(label)[0]] || "No Change";
                        }
                    });
                }

                const baseFoundationLabel =
                    getUpgradeEnvelopeRowLabels({
                        codes,
                        components,
                        rowId: "basementWall",
                        upgradePackage: {},
                    })?.["label"] || "None";

                generatedValues["nzBeforeFoundation"] = baseFoundationLabel;
                generatedValues["nzAfterFoundation"] = upgradedFoundationLabel;
            }

            if (rowId === "window") {
                let upgradedWindowLabel = "No Change";

                if (!isEmpty(eFileBaseModelData)) {
                    upgradedWindowLabel =
                        getUpgradeEnvelopeRowLabels({
                            codes: eFileBaseModelData?.codes || {},
                            components: eFileBaseModelData?.components || {},
                            rowId: "window",
                            upgradePackage: {},
                        })?.["label"] || "None";
                } else {
                    Object.keys(upgrades).forEach((key) => {
                        const { label = {}, upgradeType = "" } = upgrades[key] || {};

                        if (upgradeType === "window") {
                            upgradedWindowLabel = label[Object.keys(label)[0]] || "No Change";
                        }
                    });
                }

                const baseWindowLabel =
                    getUpgradeEnvelopeRowLabels({
                        codes,
                        components,
                        rowId: "window",
                        upgradePackage: {},
                    })?.["label"] || "None";

                generatedValues["nzBeforeWindow"] = baseWindowLabel;
                generatedValues["nzAfterWindow"] = upgradedWindowLabel;
            }

            if (rowId === "ventilation") {
                let upgradedVentilationLabel = "No Change";

                if (!isEmpty(eFileBaseModelData)) {
                    upgradedVentilationLabel =
                        getUpgradeHvacRowLabels({
                            heatingCooling: eFileBaseModelData?.heatingCooling || {},
                            ventilation: eFileBaseModelData?.ventilation || {},
                            rowId: "ventilation",
                            upgradePackage: {},
                        })?.["label"] || "None";
                } else {
                    Object.keys(upgrades).forEach((key) => {
                        const { label = {}, upgradeType = "" } = upgrades[key] || {};

                        if (upgradeType === "ventilation") {
                            upgradedVentilationLabel = label[Object.keys(label)[0]] || "No Change";
                        }
                    });
                }

                const baseVentilationLabel =
                    getUpgradeHvacRowLabels({
                        heatingCooling,
                        ventilation,
                        rowId: "ventilation",
                        upgradePackage: {},
                    })?.["label"] || "None";

                generatedValues["nzBeforeVentilation"] = baseVentilationLabel;
                generatedValues["nzAfterVentilation"] = upgradedVentilationLabel;
            }

            if (rowId === "spaceHeating" || rowId === "spaceCooling") {
                let upgradedHeatingCoolingLabel = "No Change";

                if (!isEmpty(eFileBaseModelData)) {
                    upgradedHeatingCoolingLabel =
                        getUpgradeHvacRowLabels({
                            heatingCooling: eFileBaseModelData?.heatingCooling || {},
                            ventilation: eFileBaseModelData?.ventilation || {},
                            rowId: rowId,
                            upgradePackage: {},
                        })?.["label"] || "None";
                } else {
                    Object.keys(upgrades).forEach((key) => {
                        const { label = {}, upgradeType = "" } = upgrades[key] || {};

                        if (upgradeType === "heatingCooling") {
                            upgradedHeatingCoolingLabel = label[rowId] || "No Change";
                        }
                    });
                }

                const baseHeatingCoolingLabel =
                    getUpgradeHvacRowLabels({
                        heatingCooling,
                        ventilation,
                        rowId,
                        upgradePackage: {},
                    })?.["label"] || "None";

                const cellId = rowId === "spaceHeating" ? "Heating" : "Cooling";

                generatedValues[`nzBefore${cellId}`] = baseHeatingCoolingLabel;
                generatedValues[`nzAfter${cellId}`] = upgradedHeatingCoolingLabel;
            }

            if (rowId === "domesticHotWater") {
                let upgradedDhwLabel = "No Change";

                if (!isEmpty(eFileBaseModelData)) {
                    upgradedDhwLabel =
                        getUpgradeDhwRowLabels({
                            domesticHotWater: eFileBaseModelData?.domesticHotWater || {},
                            heatingCooling: eFileBaseModelData?.heatingCooling || {},
                            rowId: "domesticHotWater",
                            upgradePackage: {},
                        })?.["label"] || "None";
                } else {
                    Object.keys(upgrades).forEach((key) => {
                        const { label = {}, upgradeType = "" } = upgrades[key] || {};

                        if (upgradeType === "domesticHotWater") {
                            upgradedDhwLabel = label[Object.keys(label)[0]] || "No Change";
                        }
                    });
                }

                const baseDhwLabel =
                    getUpgradeDhwRowLabels({
                        domesticHotWater,
                        heatingCooling,
                        rowId,
                        upgradePackage: {},
                    })?.["label"] || "None";

                generatedValues["nzBeforeDHW"] = baseDhwLabel;
                generatedValues["nzAfterDHW"] = upgradedDhwLabel;
            }
        });

        return generatedValues;
    }, [
        afterBuildingCondition,
        afterComfort,
        airTightness,
        baseLoads,
        beforeBuildingCondition,
        beforeComfort,
        codes,
        components,
        domesticHotWater,
        generation,
        heatingCooling,
        program,
        preRetrofitResultsData,
        postRetrofitResultsData,
        upgradePackage,
        ventilation,
    ]);

    const restoreDefaultContent = () => {
        updateNzStudyPdfData("nzPage2Content", initContent);
    };

    useEffect(() => {
        // Fill default content if the selected upgrade is changed or page content is empty
        if (isEmpty(nzPage2Content) || currentSelectedUpgrade !== selectedUpgrade) {
            restoreDefaultContent();
            setCurrentSelectedUpgrade(selectedUpgrade);
        }
    }, [selectedUpgrade]);

    return (
        <div>
            <Table>
                <TableHead className={classes.tableHeader}>
                    <TableRow>
                        <TableCell> </TableCell>
                        <TableCell> BEFORE </TableCell>
                        <TableCell> AFTER </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody className={classes.tableBody}>
                    {tableRows.map((row, index) => (
                        <TableRow>
                            {row.map((cellId, cellIndex) => (
                                <TableCell
                                    key={cellId + cellIndex}
                                    style={{
                                        fontWeight: cellIndex === 0 ? "bold" : "normal",
                                        background: index % 2 === 0 && "#E6E7E9",
                                    }}
                                >
                                    <div>
                                        {cellId === "beforeComfort" && (
                                            <Textarea
                                                placeholder={"Describe Initial Building Comfort"}
                                                value={beforeComfort}
                                                minHeight={"100px"}
                                                onChange={(e) =>
                                                    updateNzStudyPdfData("beforeComfort", e.target.value.slice(0, 100))
                                                }
                                                withLimit={true}
                                                limit={100}
                                                resize={"vertical"}
                                                hideLabel
                                            />
                                        )}
                                        {cellId === "afterComfort" && (
                                            <Textarea
                                                placeholder={"Describe Final Building Comfort"}
                                                value={afterComfort}
                                                minHeight={"100px"}
                                                onChange={(e) =>
                                                    updateNzStudyPdfData("afterComfort", e.target.value.slice(0, 100))
                                                }
                                                withLimit={true}
                                                limit={100}
                                                resize={"vertical"}
                                                hideLabel
                                            />
                                        )}
                                        {cellId === "beforeBuildingCondition" && (
                                            <Textarea
                                                placeholder={"Describe Initial Building Condition"}
                                                value={beforeBuildingCondition}
                                                minHeight={"100px"}
                                                onChange={(e) =>
                                                    updateNzStudyPdfData(
                                                        "beforeBuildingCondition",
                                                        e.target.value.slice(0, 100)
                                                    )
                                                }
                                                withLimit={true}
                                                limit={100}
                                                resize={"vertical"}
                                                hideLabel
                                            />
                                        )}
                                        {cellId === "afterBuildingCondition" && (
                                            <Textarea
                                                placeholder={"Describe Final Building Condition"}
                                                value={afterBuildingCondition}
                                                minHeight={"100px"}
                                                onChange={(e) =>
                                                    updateNzStudyPdfData(
                                                        "afterBuildingCondition",
                                                        e.target.value.slice(0, 100)
                                                    )
                                                }
                                                withLimit={true}
                                                limit={100}
                                                resize={"vertical"}
                                                hideLabel
                                            />
                                        )}
                                        {cellId === "beforeElectricalPanelSize" && (
                                            <Input
                                                hideLabel
                                                placeholder={"Initial Size"}
                                                type={"number"}
                                                minHeight={"100px"}
                                                input={{
                                                    value: beforeElectricalPanelSize,
                                                    onChange: (e) =>
                                                        updateNzStudyPdfData("beforeElectricalPanelSize", e),
                                                }}
                                            />
                                        )}
                                        {cellId === "afterElectricalPanelSize" && (
                                            <Input
                                                hideLabel
                                                placeholder={"Final Size"}
                                                type={"number"}
                                                minHeight={"100px"}
                                                input={{
                                                    value: afterElectricalPanelSize,
                                                    onChange: (e) =>
                                                        updateNzStudyPdfData("afterElectricalPanelSize", e),
                                                }}
                                            />
                                        )}
                                        {cellIndex === 0
                                            ? cellId.toUpperCase()
                                            : !cellId.includes("Comfort") &&
                                              !cellId.includes("BuildingCondition") &&
                                              !cellId.includes("ElectricalPanelSize") && (
                                                  <Input
                                                      placeholder={initContent[cellId]}
                                                      input={{
                                                          value: nzPage2Content[cellId],
                                                          onChange: (value) => {
                                                              const newContent = { ...nzPage2Content, [cellId]: value };
                                                              updateNzStudyPdfData("nzPage2Content", newContent);
                                                          },
                                                      }}
                                                      disabled={
                                                          cellId.includes("Energy") || cellId.includes("HeatLossGain")
                                                      }
                                                      hideLabel
                                                  />
                                              )}
                                    </div>
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <div className={classes.buttonContainer}>
                <Button className={classes.button} onClick={restoreDefaultContent}>
                    Restore Defaults
                </Button>
            </div>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    results: selectResults,
    modelData: selectModelData,
    upgradePackages: selectUpgradePackages,
    nzStudyReportPdfData: selectNzStudyReportPdfData,
});

const mapDispatchToProps = (dispatch) => ({
    updateNzStudyPdfData: (fieldName, value) => dispatch(updateNzStudyPdfData(fieldName, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NetZeroPlanTable);
